/**
 * Generic data detail page for descriptor-driven resources.
 *
 * Fetches a single record and presents it through canonical page, state,
 * localization, and table-formatting primitives. Unknown fields receive
 * readable fallback labels; raw backend errors are never rendered directly.
 */
import { useParams, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PageLoadingState, PageNotFoundState } from '../states';
import { ResourceLoadErrorState } from './ResourceLoadErrorState';
import { Page, PageSection } from '../layout';
import { useAppI18n } from '../../i18n/use-app-i18n';
import { translateRuntimeTKeyOrFallback } from '../../i18n/resolve-key';
import type { Locale, TableColumnDescriptor, TableDescriptor, TranslationKey } from '@finance/contracts';
import { formatColumnValue } from './table/table-value-format';

interface GetHookResult {
  data?: unknown;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null | unknown;
  refetch: () => void;
}

interface DataDetailPageProps {
  titleKey: TranslationKey | string;
  backPath: string;
  paramName?: string;
  useGet: (id: string) => GetHookResult;
  /** Context-owned resources resolve the target from auth context, not a URL parameter. */
  allowEmptyId?: boolean;
  descriptor?: TableDescriptor;
  children?: ReactNode;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isScalar(value: unknown): boolean {
  return value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function humanizeField(field: string): string {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, letter => letter.toUpperCase());
}

function DetailValue({ value, depth = 0 }: { value: unknown; depth?: number }): ReactNode {
  const { text, translator } = useAppI18n();
  if (value == null || value === '') return <Typography color="text.disabled">—</Typography>;
  if (typeof value === 'boolean') {
    return <Typography>{value ? text('opt.boolean.true') : text('opt.boolean.false')}</Typography>;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return <Typography sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{String(value)}</Typography>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <Typography color="text.disabled">—</Typography>;
    if (value.every(isScalar)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((item, index) => (
            <Chip
              key={`${String(item)}-${index}`}
              label={item == null ? '—' : String(item)}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      );
    }
    return (
      <Stack spacing={1}>
        {value.map((item, index) =>
          isRecord(item) ? (
            <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
              <DetailRecord record={item} depth={depth + 1} />
            </Paper>
          ) : (
            <DetailValue key={index} value={item} depth={depth + 1} />
          ),
        )}
      </Stack>
    );
  }
  if (isRecord(value)) {
    return (
      <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
        <DetailRecord record={value} depth={depth + 1} />
      </Box>
    );
  }
  return <Typography>{String(value)}</Typography>;
}

interface DetailColumn {
  field: string;
  labelKey?: TranslationKey;
  descriptor?: TableColumnDescriptor;
}

interface DetailRecordProps {
  record: Record<string, unknown>;
  depth?: number;
  columns?: DetailColumn[];
  locale?: Locale;
}

function DetailRecord({ record, depth = 0, columns, locale = 'en' }: DetailRecordProps): ReactNode {
  const { text } = useAppI18n();
  const fields: DetailColumn[] = columns ?? Object.keys(record)
    .filter(key => record[key] !== undefined)
    .map(field => ({ field }));

  return (
    <Grid container spacing={2}>
      {fields.map(column => {
        const value = record[column.field];
        if (value === undefined && columns) return null;
        const label = column.labelKey
          ? text(column.labelKey)
          : humanizeField(column.field);
        const displayValue = column.descriptor && isScalar(value)
          ? formatColumnValue(value, column.descriptor, { locale, row: record })
          : value;
        return (
          <Grid size={{ xs: 12, md: depth > 0 ? 12 : 6 }} key={column.field}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
              {label}
            </Typography>
            <DetailValue value={displayValue} depth={depth} />
          </Grid>
        );
      })}
    </Grid>
  );
}

export function DataDetailPage({
  titleKey,
  backPath,
  paramName = 'id',
  useGet,
  allowEmptyId,
  descriptor,
  children,
}: DataDetailPageProps) {
  const { text, translator, language } = useAppI18n();
  const locale: Locale = language?.startsWith('ne') ? 'ne' : 'en';
  const navigate = useNavigate();
  const params = useParams<Record<string, string>>();
  const id = params[paramName] ?? '';
  const { data, isLoading, isError, error, refetch } = useGet(id);

  if (!id && !allowEmptyId) return <PageNotFoundState />;
  if (isLoading) return <PageLoadingState />;
  if (isError) return <ResourceLoadErrorState error={error} onRetry={refetch} />;
  if (!data) return <PageNotFoundState />;

  const record = isRecord(data) ? data : {};
  const columns = descriptor?.columns.map(column => ({
    field: column.field,
    labelKey: column.labelKey,
    descriptor: column,
  }));

  return (
    <Page
      title={translateRuntimeTKeyOrFallback(translator, titleKey)}
      actionNode={(
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backPath)}
        >
          {text('common.back')}
        </Button>
      )}
    >
      <PageSection variant="surface">
        <DetailRecord record={record} columns={columns} locale={locale} />
      </PageSection>
      {children}
    </Page>
  );
}
